// Route stub created by tools/gen_web_routes.py from contract/catalog/screens.json; owned by hand from here on.
// The playground (E21U1, board `dev-playground`), the Compose PlaygroundScreen's twin: one component on its stage, the
// stage's Theme and Text size (100, 130, 200 %) applying to the stage alone, and its code in React and Compose tabs to
// copy. Text size zooms the stage: the kit's sizes are in rem, which follow the page's root, not the stage. An id the
// playground does not hold shows its empty state. Words verbatim (a developer screen), as the gallery's.
import { ids } from '@orca/api-client/testIds'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { SegmentedControl } from '../../components/ui/segmented-control'
import { showSnackbar } from '../../components/ui/snackbar'
import { TabPanel, Tabs } from '../../components/ui/tabs'
import { playgroundEntries } from '../../features/devtools/playground'

export const Route = createFileRoute('/gallery/$componentId')({
  staticData: { screenId: 'PlaygroundScreen', access: 'dev builds' },
  component: PlaygroundScreen,
})

const TEXT_SIZES = ['100', '130', '200']
const codeTabs = [
  { value: 'react', label: 'React', testId: 'playground_reactTab' },
  { value: 'compose', label: 'Compose', testId: 'playground_composeTab' },
]

function pageTheme(): string {
  const chosen = document.documentElement.dataset.theme
  if (chosen) return chosen
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function PlaygroundScreen() {
  const { componentId } = Route.useParams()
  const entry = playgroundEntries.find((it) => it.id === componentId)
  // The stage starts in the page's theme: the system's unless the person chose one.
  const [theme, setTheme] = useState(pageTheme)
  const [size, setSize] = useState(TEXT_SIZES[0] ?? '100')
  const [tab, setTab] = useState('react')
  const copy = (code: string) => {
    navigator.clipboard
      ?.writeText(code)
      .then(() => showSnackbar({ message: 'Code copied', tone: 'positive' }))
      .catch(() => {})
  }
  return (
    <main
      data-testid={ids.playground.root}
      className="mx-auto flex w-full max-w-(--reading-width-expanded) flex-col gap-(--stack-lg) p-(--inset-lg)"
    >
      <header className="flex flex-wrap items-center gap-(--inline-md)">
        <h1 className="flex-1 font-bold text-title-lg">{entry?.name ?? componentId}</h1>
        <Link to="/gallery" className="font-semibold text-link">
          Gallery
        </Link>
      </header>
      {!entry ? (
        <Card data-testid={ids.playground.empty} className="flex flex-col gap-(--stack-sm) p-(--inset-lg)">
          <strong>No component {componentId}</strong>
          <span className="text-fg-muted">The playground holds {playgroundEntries.map((it) => it.id).join(', ')}.</span>
        </Card>
      ) : (
        <>
          <div
            data-testid={ids.playground.previewCard}
            data-theme={theme}
            className="overflow-auto rounded-(--radius-dialog) border border-line bg-canvas p-(--inset-lg) text-fg"
          >
            <div style={{ zoom: Number(size) / 100 }}>{entry.stage()}</div>
          </div>
          <div data-testid={ids.playground.controlsGroup} className="flex flex-wrap gap-(--inline-lg)">
            <SegmentedControl
              label="Theme, the stage only"
              options={[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
              value={theme}
              onChange={setTheme}
              testId="playground_themeGroup"
            />
            <SegmentedControl
              label="Text size, the stage only"
              options={TEXT_SIZES.map((it) => ({ value: it, label: `${it} %` }))}
              value={size}
              onChange={setSize}
              testId={ids.playground.textSizeGroup}
            />
          </div>
          <Tabs tabs={codeTabs} value={tab} onValueChange={setTab} label="Code">
            {codeTabs.map(({ value }) => {
              const code = value === 'react' ? entry.react : entry.compose
              return (
                <TabPanel key={value} value={value} className="flex flex-col gap-(--stack-sm) pt-(--stack-md)">
                  <pre
                    data-testid={`playground_${value}Code`}
                    className="overflow-x-auto rounded-(--radius-md) bg-sunken p-(--inset-md) font-mono text-caption"
                  >
                    <code>{code}</code>
                  </pre>
                  <Button variant="secondary" size="sm" className="self-start" onClick={() => copy(code)}>
                    Copy
                  </Button>
                </TabPanel>
              )
            })}
          </Tabs>
        </>
      )}
    </main>
  )
}
